import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { VirtualSensor, VirtualSensorsService } from '@services/virtual-sensors.service';
import { DataPoint } from '@services/datapoints-data.service';
import { Account } from '@services/accounts.service';
import { Organisation } from '@services/organisation-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EditVirtualSensorComponent, editVirtualSensorDialogData } from 'src/app/dialogs/edit-virtual-sensor/edit-virtual-sensor.component';
import { MatDialog } from '@angular/material/dialog';
import { AddVirtualSensorComponent, AddVirtualSensorDialogData } from 'src/app/dialogs/add-virtual-sensor/add-virtual-sensor.component';

@Component({
	selector: 'app-virtual-sensors',
	templateUrl: './virtual-sensors.component.html',
	styleUrl: './virtual-sensors.component.scss'
})
export class VirtualSensorsComponent implements OnInit, OnChanges, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** AccountGUID */
	public AccountGUID: string;

	/** OrganisationUID */
	public OrganisationUID: string;

	/** Asset */
	@Input() asset: LevelAsset;

	/** currentUser */
	public currentUser: WebAppUser;

	/** account */
	public account: Account;

	/** organisation */
	public organisation: Organisation;

	/** virtualSensors */
	public virtualSensors: VirtualSensor[] = [];

	/** displayedColumns */
	displayedColumns: string[] = ['VirtualSensorName', 'InputDataPoints', 'Conversion', 'Edit', 'Delete'];

	/** datasource */
	dataSource: MatTableDataSource<VirtualSensor>;

	/** Table paginator */
	@ViewChild(MatPaginator) paginator: MatPaginator;

	/** Table sorting */
	@ViewChild(MatSort) sort: MatSort;

	/** assetVirtualSensors */
	public assetVirtualSensors: VirtualSensor[] = [];

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService 
	 * @param globalFunctions globalFunctions 
	 * @param authenticationService authenticationService 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		private router: Router,
		private virtualSensorsService: VirtualSensorsService,
		public dialog: MatDialog,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, account, selectedOrganisationUID, selectedOrganisation, hierarchicalData, currentUser]) =>
				!!selectedAccountGUID && !!account && !!selectedOrganisationUID && !!selectedOrganisation && !!hierarchicalData && !!currentUser)
		).subscribe({
			next: ([selectedAccountGUID, account, selectedOrganisationUID, selectedOrganisation, hierarchicalData, currentUser]) => {
				this.AccountGUID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.account = account;
				this.organisation = selectedOrganisation;

				this.getVirtualSensors();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * ngOnChanges
	 * @param changes changes 
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['asset'] && !changes['asset'].firstChange) {
			this.getAssetVirtualSensors();
		}
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
	}

	/**
	 * getVirtualSensors
	 */
	getVirtualSensors() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.getVirtualSensor$(this.AccountGUID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((virtualSensors) => !!virtualSensors),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.dataSource = new MatTableDataSource(this.virtualSensors);
				this.paginator = this.dataSource.paginator!;
				this.sort = this.dataSource.sort!;
			})
		).subscribe({
			next: (virtualSensors) => {
				this.virtualSensors = virtualSensors;
				this.virtualSensors.forEach((vs: VirtualSensor) => {
					vs._dataPoints = JSON.parse(vs.DataPoints ?? '[]').map((x: DataPoint) => (
						{
							value: `[${this.getValueForDP(x, vs)}]`,
							datapoint: x
						}
					));
				});
				if (this.asset) {
					this.getAssetVirtualSensors();
				}
			},
			error: (error) => {
				if (error.status === 404) {
					this.virtualSensors = [];
				} else {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.getVirtualSensor$", false);
				}
			},
		});
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	goToVirtualSensor(virtualSensor: VirtualSensor) {
		this.router.navigate([`./setup/virtual-datapoint-management/${this.OrganisationUID}/virtual-datapoint/${virtualSensor.VirtualSensorID}`]);
	}

	/**
	 * editVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	editVirtualSensor(virtualSensor: VirtualSensor) {
		let dialogData: editVirtualSensorDialogData = {
			virtualSensor: virtualSensor
		}

		const dialogRef = this.dialog.open(EditVirtualSensorComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe((result: any) => {
			if (result) {
				virtualSensor.VirtualSensorName = result.virtualSensorName;
				virtualSensor.InputDataPoints = result.inputDatapoints;
				virtualSensor.Conversion = result.conversion;
				virtualSensor.SI = result.SI;
				const datapointIDs: string = Object.entries(JSON.parse(result.inputDatapoints)).map((x) => x[1]).toString();
				virtualSensor.DataPointIDs = datapointIDs;
				this.patchVirtualSensor(virtualSensor);
			}
		});
	}

	/**
	 * createVirtualSensor
	 */
	createVirtualSensor() {
		let dialogData: editVirtualSensorDialogData = {

		}

		const dialogRef = this.dialog.open(EditVirtualSensorComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe((result: any) => {
			if (result) {
				const datapointIDs: string = Object.entries(JSON.parse(result.inputDatapoints)).map((x) => x[1]).toString();
				let newVS: VirtualSensor = {
					VirtualSensorID: 0,
					AccountGUID: '',
					OrganisationUID: '',
					InputDataPoints: result.inputDatapoints,
					Conversion: result.conversion,
					VirtualSensorName: result.virtualSensorName,
					AssetIDs: '',
					DataPointIDs: datapointIDs,
					DataPoints: '',
					SI: result.SI
				};
				this.postVirtualSensor(newVS);
			}
		});
	}

	/**
	 * postVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	postVirtualSensor(virtualSensor: VirtualSensor) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.postVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensor).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				this.getVirtualSensors();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.postVirtualSensor$", false);
				}
			},
		});
	}

	/**
	 * patchVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	patchVirtualSensor(virtualSensor: VirtualSensor, overrideMessage?: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.patchVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensor.VirtualSensorID, virtualSensor).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: overrideMessage ? overrideMessage : result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				this.getVirtualSensors();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.patchVirtualSensor$", false);
				}
			},
		});
	}

	/**
	 * getValueForDP
	 * @param datapoint datapoint 
	 */
	getValueForDP(datapoint: DataPoint, virtualSensor: VirtualSensor): number {
		return parseInt(Object.entries(JSON.parse(virtualSensor.InputDataPoints)).find((item) => item[1] == `${datapoint.DataPointID}`)?.[0] ?? "0");
	}

	/**
	 * getAssetVirtualSensors
	 */
	getAssetVirtualSensors() {
		if (this.asset) {
			this.assetVirtualSensors = this.virtualSensors.filter((vs: VirtualSensor) => vs.AssetIDs.split(",").includes(this.asset.AssetID.toString()));
		}
	}

	/**
	 * removeVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	removeVirtualSensor(virtualSensor: VirtualSensor) {
		if (this.asset) {
			virtualSensor.AssetIDs = virtualSensor.AssetIDs.split(",").filter((assetID: string) => assetID != this.asset.AssetID.toString()).toString();
			this.patchVirtualSensor(virtualSensor, "Virtual DataPoint Removed");
		}
	}

	/**
	 * addVirtualSensor
	 */
	addVirtualSensor() {
		if (this.asset) {
			let dialogData: AddVirtualSensorDialogData = {
				virtualSensors: this.virtualSensors,
				asset: this.asset
			}

			const dialogRef = this.dialog.open(AddVirtualSensorComponent, {
				data: dialogData
			});
			dialogRef.afterClosed().pipe(
			).subscribe((result: any) => {
				if (result) {
					result.forEach((vs: VirtualSensor) => {
						let newIDs: string[] = vs.AssetIDs.split(",");
						if (!newIDs.includes(this.asset.AssetID.toString())) {
							newIDs.push(this.asset.AssetID.toString());
						}
						newIDs = newIDs.filter((s: string) => s != '');
						vs.AssetIDs = newIDs.toString();
						this.patchVirtualSensor(vs, "Virtual DataPoint Added");
					});
				}
			});
		}
	}

	/**
	 * requestDeleteVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	requestDeleteVirtualSensor(virtualSensor: VirtualSensor) {
		this.globalFunctions.UserConfirm$(`Delete ${virtualSensor.VirtualSensorName}?`).subscribe(async OKResult => {
			if (OKResult) {
				this.deleteVirtualSensor(virtualSensor);
			}
		});
	}

	/**
	 * deleteVirtualSensor
	 * @param virtualSensor virtualSensor 
	 */
	deleteVirtualSensor(virtualSensor: VirtualSensor) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.virtualSensorsService.deleteVirtualSensor$(this.AccountGUID, this.OrganisationUID, virtualSensor.VirtualSensorID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((result) => !!result),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.globalFunctions.DisplaySnackBar({
					message: result[0].Result.replace("OK - ", ""),
					duration: 2000,
					action: ""
				});
				this.getVirtualSensors();
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.virtualSensorsService.deleteVirtualSensor$", false);
				}
			},
		});
	}
}
